// src/eden/planner/plannerRevisionRouting.js
//
// Eden conversational routing into the Sherloc Media Planner revision flow.
//
// From Eden Entry / Command Center a user can say "Let's revise the media plan
// for <advertiser or campaign>". This module is the pure, network-free brain:
//   - detect the revision intent + extract the named entity
//   - resolve the entity against the campaign list (advertisers derived from it)
//   - decide: open one campaign, ask which campaign, offer options, or none
//   - interpret a clarification reply against pending options
//   - produce calm response copy + the navigation state for the planner
//
// Guardrails: this only ever NAVIGATES + pre-selects. It never generates a
// strategy, saves, publishes, sends a microsite, or activates a campaign.

export const PLANNER_ROUTE = "/sherloc-media-planner";

// Campaign list endpoint already used by Media Campaigns / the planner.
export const MEDIA_CAMPAIGNS_PATH = "/api/media-campaigns";

const REVISION_TRIGGERS = [
  /\brevise\b/i,
  /\bupdate\b/i,
  /\bedit\b/i,
  /\brefresh\b/i,
  /\bopen\b/i,
  /\bwork on\b/i,
  /\bre-?do\b/i,
];

const PLANNER_NOUNS =
  /\b(media\s*plan|media\s*planner|sherloc\s*planner|planner|campaign|strategy)\b/i;

function str(value) {
  return value === undefined || value === null ? "" : String(value);
}

function norm(value) {
  return str(value).trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Detect a planner-revision intent and extract the named entity phrase.
 * @returns {{ matched: boolean, entity: string }}
 */
export function detectPlannerRevisionIntent(text) {
  const value = str(text).trim();
  if (!value) return { matched: false, entity: "" };

  const hasTrigger = REVISION_TRIGGERS.some((re) => re.test(value));
  const hasPlannerNoun = PLANNER_NOUNS.test(value);
  // Require an action trigger AND a planner/campaign noun so we don't hijack
  // generic chat like "update me on performance".
  if (!hasTrigger || !hasPlannerNoun) return { matched: false, entity: "" };

  const entity = extractEntityPhrase(value);
  return { matched: true, entity };
}

/**
 * Extract the advertiser/campaign phrase after a connector such as
 * "for", "on", "campaign", "advertiser", or "media plan for".
 */
export function extractEntityPhrase(text) {
  const value = str(text).trim();
  const patterns = [
    /\bmedia plan(?:ner)?\s+for\s+(.+)$/i,
    /\bcampaign\s+for\s+(.+)$/i,
    /\bfor\s+(.+)$/i,
    /\bon\s+(.+)$/i,
    /\badvertiser\s+(.+)$/i,
    // "Update the <name> campaign" / "edit <name> campaign"
    /\b(?:revise|update|edit|refresh|open|work on|redo|re-do)\s+(?:the\s+)?(.+?)\s+campaign\b/i,
    /\bthe\s+(.+?)\s+campaign\b/i,
  ];
  for (const re of patterns) {
    const m = value.match(re);
    if (m && m[1]) {
      return cleanEntity(m[1]);
    }
  }
  return "";
}

function cleanEntity(raw) {
  return str(raw)
    .trim()
    .replace(/^["'“”]+|["'“”]+$/g, "")
    .replace(/[.?!,;:]+$/g, "")
    .replace(/^(?:let'?s\s+)?(?:revise|update|edit|refresh|open|work on|redo|re-do)\s+(?:the\s+)?/i, "")
    .replace(/\s+(please|now|thanks|thank you)$/i, "")
    .replace(/\bmedia plan(?:ner)?\b/i, "")
    .replace(/\bcampaign\b\s*$/i, "")
    .trim();
}

// Tolerant field readers — campaign list shapes vary by endpoint.
export function campaignId(c) {
  return c?.id ?? c?._id ?? c?.campaign_id ?? c?.campaignId ?? null;
}
export function campaignName(c) {
  return str(c?.campaign_name || c?.campaignName || c?.name || c?.title || "").trim();
}
export function campaignAdvertiserId(c) {
  return c?.advertiser_id ?? c?.advertiserId ?? c?.advertiserID ?? c?.advertiser?.id ?? null;
}
export function campaignAdvertiserName(c) {
  const adv = c?.advertiser;
  return str(
    c?.advertiser_name ||
      c?.advertiserName ||
      (typeof adv === "string" ? adv : adv?.name) ||
      ""
  ).trim();
}

function toOption(c) {
  return {
    id: campaignId(c) != null ? String(campaignId(c)) : null,
    campaignName: campaignName(c),
    advertiserId: campaignAdvertiserId(c) != null ? String(campaignAdvertiserId(c)) : null,
    advertiserName: campaignAdvertiserName(c),
  };
}

/**
 * Resolve the named entity against the campaign list.
 * @returns {{ status: "campaign"|"advertiser_multi"|"multi"|"none",
 *   entity: string, match?: object, advertiserName?: string, options: object[] }}
 */
export function resolvePlannerRevisionTarget(entity, campaigns = []) {
  const cleanEntityValue = norm(entity);
  const list = Array.isArray(campaigns) ? campaigns.map(toOption).filter((o) => o.id) : [];

  if (!cleanEntityValue || !list.length) {
    return { status: "none", entity: str(entity).trim(), options: [] };
  }

  // 1) Exact campaign-name match wins.
  const exactCampaign = list.filter((o) => norm(o.campaignName) === cleanEntityValue);
  if (exactCampaign.length === 1) {
    return { status: "campaign", entity: str(entity).trim(), match: exactCampaign[0], options: [] };
  }
  if (exactCampaign.length > 1) {
    return { status: "multi", entity: str(entity).trim(), options: exactCampaign };
  }

  // 2) Exact advertiser-name match.
  const exactAdvertiser = list.filter((o) => norm(o.advertiserName) === cleanEntityValue);
  if (exactAdvertiser.length === 1) {
    return { status: "campaign", entity: str(entity).trim(), match: exactAdvertiser[0], options: [] };
  }
  if (exactAdvertiser.length > 1) {
    return {
      status: "advertiser_multi",
      entity: str(entity).trim(),
      advertiserName: exactAdvertiser[0].advertiserName,
      options: exactAdvertiser,
    };
  }

  // 3) Fuzzy/partial — always offered as options, never silently chosen.
  const fuzzy = list.filter((o) => {
    const name = norm(o.campaignName);
    const adv = norm(o.advertiserName);
    return (
      (name && (name.includes(cleanEntityValue) || cleanEntityValue.includes(name))) ||
      (adv && (adv.includes(cleanEntityValue) || cleanEntityValue.includes(adv)))
    );
  });
  if (fuzzy.length) {
    return { status: "multi", entity: str(entity).trim(), options: fuzzy };
  }

  return { status: "none", entity: str(entity).trim(), options: [] };
}

/**
 * Interpret a user's clarification reply against pending campaign options.
 * Accepts an exact/contained campaign name, or an ordinal ("1", "first").
 * @returns {object|null} the chosen option, or null if ambiguous/no match.
 */
export function matchClarificationChoice(text, options = []) {
  const value = norm(text);
  const list = Array.isArray(options) ? options : [];
  if (!value || !list.length) return null;

  // Ordinal selection: "1", "option 2", "first", "second", "third".
  const ordinalWords = { first: 1, second: 2, third: 3, fourth: 4, fifth: 5 };
  const numeric = value.match(/\b(?:option\s*)?(\d{1,2})\b/);
  let index = numeric ? Number(numeric[1]) : null;
  if (!index) {
    for (const [word, n] of Object.entries(ordinalWords)) {
      if (new RegExp(`\\b${word}\\b`).test(value)) {
        index = n;
        break;
      }
    }
  }
  if (index && index >= 1 && index <= list.length) {
    return list[index - 1];
  }

  // Exact campaign-name match.
  const exact = list.filter((o) => norm(o.campaignName) === value);
  if (exact.length === 1) return exact[0];

  // Contained match (unique only — never guess between several).
  const contained = list.filter((o) => {
    const name = norm(o.campaignName);
    return name && (value.includes(name) || name.includes(value));
  });
  if (contained.length === 1) return contained[0];

  return null;
}

/** Navigation state the planner reads on mount to pre-select + load. */
export function buildPlannerRevisionNavState(match) {
  return {
    advertiserId: match?.advertiserId ?? null,
    advertiserName: match?.advertiserName || "",
    campaignId: match?.id ?? null,
    campaignName: match?.campaignName || "",
    mode: "revise",
  };
}

function optionLabel(option) {
  const name = option.campaignName || "Untitled campaign";
  return option.advertiserName ? `${name} (${option.advertiserName})` : name;
}

export function revisionSuccessCopy(match) {
  const campaign = match?.campaignName || "this campaign";
  return match?.advertiserName
    ? `Opening Sherloc Media Planner for ${match.advertiserName} — ${campaign}.`
    : `Opening Sherloc Media Planner for ${campaign}.`;
}

export function revisionClarifyCopy(result) {
  const options = result?.options || [];
  const lines = options.map((o, i) => `${i + 1}. ${optionLabel(o)}`);
  if (result?.status === "advertiser_multi" && result.advertiserName) {
    return [
      `I found ${result.advertiserName}. Which campaign should I open?`,
      ...lines,
    ].join("\n");
  }
  return [
    `I found multiple matches for “${result?.entity || "that"}”. Which one would you like to revise?`,
    ...lines,
  ].join("\n");
}

export const REVISION_NOT_FOUND_COPY =
  "I couldn’t find that advertiser or campaign yet. Try the exact advertiser or campaign name, or open Media Campaigns to choose one.";

/** Fetch the campaign list from the existing endpoint (tolerant of shape). */
export async function fetchPlannerRevisionCampaigns({ baseUrl, token, fetchImpl } = {}) {
  const doFetch = fetchImpl || (typeof fetch !== "undefined" ? fetch : null);
  const base = str(baseUrl).replace(/\/+$/, "");
  if (!doFetch || !base) return [];
  try {
    const res = await doFetch(`${base}${MEDIA_CAMPAIGNS_PATH}`, {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      cache: "no-store",
    });
    if (!res?.ok) return [];
    const data = await res.json().catch(() => ({}));
    if (Array.isArray(data?.campaigns)) return data.campaigns;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  } catch {
    return [];
  }
}
